import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { PlexEnvironment } from '@app/env';
import { ProjectEstimate } from '@app/shared/models';
import { ProjectEstimateDetailItemSyncDto } from '@app/shared/models/dtos/project-estimate-detail-item-sync';
import { ErrorMessage, ErrorService } from 'app/common/services/error.service';
import { BehaviorSubject, Observable, throwError, zip } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

enum RecordStatus {
  Undefined = 'Undefined',
  Active = 'Active',
  Pending = 'Pending',
  Approved = 'Approved',
  Rejected = 'Rejected',
}

enum RecordType {
  Afe = 1,
  Pcr = 2,
  AfeRevision = 3,
}

@Injectable({
  providedIn: 'root',
})
export class ProjectEstimateService {
  private http = inject(HttpClient);
  private error = inject(ErrorService);
  public environment = inject(PlexEnvironment);

  private BASE_URL = '';
  public estimateChanged = new BehaviorSubject<boolean>(false);

  constructor() {
    this.BASE_URL = this.environment.apiUrl;
  }

  public getCcctIems(id: any): Promise<any> {
    let table: string = 'ProjectCostCodeCostTypeItems';
    let filter: string = '?$filter=ProjectId%20eq%20';
    let url = `${this.BASE_URL}${table}${filter}${id}`;
    return this.http
      .get(url)
      .toPromise()
      .then(response => response);
  }

  public removeCcctItem(id: any) {
    let url = `${this.BASE_URL}ProjectCostCodeCostTypeItems?ProjectCostCodeCostTypeItemId=${id}`;
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http
      .delete(url, { headers: headers }).pipe(
        catchError(e => this.error.error('Error in removeCcctItem', e)))
      .toPromise();
  }

  public removeEstimate(id: number) {
    let url = `${this.BASE_URL}estimation/projects/delete?ProjectEstimateId=${id}`;
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http
      .delete(url, { headers: headers }).pipe(
        catchError(e => this.error.error('Error in removeEstimate', e)))
      .toPromise();
  }

  public updateTaxAndContingency(projectId: number, version: number) {
    let endpoint: string = `estimation/projects/${projectId}/version/${version}/calculateTaxAndContingency`;
    let url = `${this.BASE_URL}${endpoint}`;
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http
      .patch(url, { headers: headers }).pipe(
        catchError(e => this.error.error('Error in updateTaxAndContingency', e)))
      .toPromise();
  }

  public async deleteDraftEstimate(projectId: number, projectEstimateId) {
    await this.removeEstimate(projectEstimateId)
      .then(o => {
        this.estimateChanged.next(true);
        this.getCcctIems(projectId)
          .then(payload => {
            payload.value.forEach(element => {
              this.removeCcctItem(element.ProjectCostCodeCostTypeItemId);
            });
          });
      });
  }

  public async removeDraftCostCodeCostTypeItems(projectId: number): Promise<void> {
    this.getCcctIems(projectId)
      .then(payload => {
        payload.value.forEach(element => {
          this.removeCcctItem(element.ProjectCostCodeCostTypeItemId);
        });
      });
  }

  public createDraftEstimate(projectId: any, version: any, standardConfigId: any, taxPercent: number) {
    let table = 'ProjectEstimates';
    let url = `${this.BASE_URL}${table}`;
    let item = {
      ProjectEstimateId: 0,
      ProjectEstimateStatusId: 1,
      ProjectId: projectId,
      EstimateVersion: version,
      EstimateTotal: 0,
      EstimateDate: new Date(),
      Tax: taxPercent,
      Contingency: 0,
      LockCalculatedItems: true
    };
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    let options = { headers: headers };
    return this.http
      .post(url, item, { headers: headers }).pipe(
        catchError(e => this.error.error('Error in createDraftEstimate', e)))
      .toPromise();
  }

  public copyEstimate(projectId: number, version: number) {
    let endpoint: string = `estimation/projects/${projectId}/ver/${version}/draftfromestimate`;
    let url = `${this.BASE_URL}${endpoint}`;
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    let options = { headers: headers };
    return this.http
      .post(url, { headers: headers }).pipe(
        catchError(e => this.error.error('Error in copyEstimate', e)))
      .toPromise();
  }

  public createNewEstimate(projectId: number) {
    let endpoint: string = `estimation/projects/${projectId}/newdraftestimate`;
    let url = `${this.BASE_URL}${endpoint}`;
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http
      .post(url, { headers: headers }).pipe(
        catchError(e => this.error.error('Error in new Draft Estimate', e)))
      .toPromise();
  }

  public updateProject(id: number, item: any) {
    let url = `${this.BASE_URL}Projects(${id})`;
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    let options = { headers: headers };
    return this.http
      .patch(url, item, { headers: headers }).pipe(
        catchError(e => this.error.error('Error in updateProject', e)))
      .toPromise();
  }

  public updateEstimate(id: number, item: any) {
    let url = `${this.BASE_URL}ProjectEstimates(${id})`;
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    let options = { headers: headers };
    return this.http
      .patch(url, item, { headers: headers }).pipe(
        catchError(e => this.error.error('Error in updateEstimate', e)))
      .toPromise();
  }

  public async patchEstimate(id: number, item: any): Promise<any> {
    let url = `${this.BASE_URL}ProjectEstimates(${id})`;
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    let options = { headers: headers };
    return this.http
      .patch(url, item, { headers: headers }).pipe(
        catchError(e => this.error.error('Error in updateEstimate', e)))
      .toPromise();
  }

  public updateProjectCcctItemsById(id: number, item: any) {
    let url = `${this.BASE_URL}ProjectCostCodeCostTypeItems(${id})`;
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    let options = { headers: headers };
    return this.http
      .patch(url, item, { headers: headers }).pipe(
        catchError(e => this.error.error('Error in updateProjectCcctItemsById', e)))
      .toPromise();
  }

  public async getEstimatesByProjectId(id: any): Promise<ProjectEstimate[]> {
    let table: string = 'ProjectEstimates';
    let expand = `?$expand=ProjectEstimateStatus,AFEUpdateReason,Project($select=AfeDocumentId)`;
    let filter: string = '&$filter=ProjectId%20eq%20';
    let sort: string = `&$orderby=EstimateVersion%20desc`;
    let url = `${this.BASE_URL}${table}${expand}${filter}${id}${sort}`;
    try {
      let result = await this.http.get<{ value: ProjectEstimate[] }>(url).toPromise().then(x => x.value);
      return result;
    } catch (e) {
      this.error.error('Error in getEstimatesByProjectId', e);
    }
    return [];
  }

  public getApprovedOrPendingAfeCount(projectId: any, approvedStatus: string, pendingStatus: string): Promise<any> {
    let table: string = `ProjectEstimates`;
    let filter: string = `?$filter=ProjectId eq ${projectId} and (RecordStatus eq '${approvedStatus}' or RecordStatus eq '${pendingStatus}')`;
    let top: string = `&$top=0`;
    let count: string = `&$count=true`;
    let orderBy: string = `&$orderby=EstimateVersion desc`;
    let url = `${this.BASE_URL}${table}${filter}${top}${count}${orderBy}`;

    return this.http.get(url).pipe(map((response: any) => {
      let count = parseInt(response['@odata.count']);
      return count;
    }),
      catchError(e => this.error.error('Error in getting estimate count', e)))
      .toPromise();
  }

  public getDraftOrRejectedEstimateCount(projectId: any, recordStatus: string, estimateStatus: number): Promise<any> {
    let table: string = `ProjectEstimates`;
    let filter: string = `?$filter=ProjectId eq ${projectId} and (RecordStatus eq '${recordStatus}' or ProjectEstimateStatusId eq ${estimateStatus})`;
    let top: string = `&$top=0`;
    let count: string = `&$count=true`;
    let url = `${this.BASE_URL}${table}${filter}${top}${count}`;

    return this.http.get(url).pipe(map((response: any) => {
      let count = parseInt(response['@odata.count']);
      return count;
    }),
      catchError(e => this.error.error('Error in getting approved afe count', e)))
      .toPromise();
  }

  public getEstimateByEstimateId(id: any): Promise<any> {
    let table: string = 'ProjectEstimates';
    let url = `${this.BASE_URL}${table}(${id})`;
    return this.http
      .get(url).pipe(
        catchError(e => this.error.error('Error in getEstimateByEstimateId', e)))
      .toPromise();
  }

  public publishDraft(endpoint: string) {
    let url = `${this.BASE_URL}${endpoint}`;
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    let options = { headers: headers };
    return this.http
      .post(url, { headers: headers }).pipe(
        catchError(e => this.error.error('Error Publishing Draft', e)))
      .toPromise();
  }

  public async startEstimateEngine(projectId: number, engineVersion: number): Promise<Object | ErrorMessage> {
    let endpoint: string = `estimation/projects/${projectId}/estimate?version=${engineVersion}`;
    let url = `${this.BASE_URL}${endpoint}`;
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http
      .post(url, { headers: headers }).pipe(
        catchError((err) => {
          let msg = (err && err.error && err.error.Message);
          if (msg) {
            return this.error.error('Estimation Engine Issue:' + msg, err);
          } else {
            return this.error.error('Error in Estimation Engine', err);
          }
        }))
      .toPromise();
  }

  public async startEstimateSync(projectId: number, dropdownKey: string, finalItemsToSync: ProjectEstimateDetailItemSyncDto): Promise<Object | ErrorMessage> {
    let endpoint: string = `estimation/projects/${projectId}/syncEstimateItems/${dropdownKey}`;
    let url = `${this.BASE_URL}${endpoint}`;
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http
      .post(url, finalItemsToSync, { headers: headers }).pipe(
        catchError(e => this.error.error('Error in syncing estimate items', e)))
      .toPromise();
  }

  public create(endpoint: string, item: any) {
    let url = `${this.BASE_URL}${endpoint}`;
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http
      .post(url, item, { headers: headers })
      .toPromise();
  }

  public runEstimatingEngine(projectId: number): Observable<any> {
    let endpoint: string = `estimation/projects/${projectId}/estimate`;
    let url = `${this.BASE_URL}${endpoint}`;
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http
      .post(url, { headers: headers }).pipe(
        map(res => res));
  }

  public syncStandardConfiguration(projectId: number): Observable<any> {
    let endpoint: string = `estimation/projects/${projectId}/syncStdConfig`;
    let url = `${this.BASE_URL}${endpoint}`;
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http
      .post(url, { headers: headers }).pipe(
        map(res => res));
  }

  public generate(endpoint: string): Promise<Object | ErrorMessage> {
    let url = `${this.BASE_URL}${endpoint}`;
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http
      .post(url, { headers: headers })
      .toPromise();
  }

  public async getAfeBudget(projectId: number): Promise<number> {
    let afeBudget: number;
    let estimateRecord: ProjectEstimate;
    const payload = await this.getEstimatesByProjectId(projectId);
    estimateRecord = payload.find(x => x.RecordStatus === RecordStatus.Approved && x.RecordTypeId === RecordType.AfeRevision);
    if (estimateRecord) {
      afeBudget = estimateRecord.EstimateTotal;
    } else {
      estimateRecord = payload.find(x => x.RecordStatus === RecordStatus.Approved && x.RecordTypeId === RecordType.Afe);
      if (estimateRecord) {
        afeBudget = estimateRecord.EstimateTotal;
      } else {
        afeBudget = 0;
      }
    }
    return afeBudget;
  }

  public postNoPayload(endpoint: string): Promise<any> {
    let url = `${this.BASE_URL}${endpoint}`;
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    let options = { headers: headers };
    return this.http
      .post(url, { headers: headers })
      .toPromise();
  }

  public getEstimateComparisonByPhaseStep(projectEstimateId: number, compareProjectEstimateId: number
    , isDraftSelected: boolean): Observable<any> {
    let endpoint: string = `estimation/projects/${projectEstimateId}/compareEstimate/${compareProjectEstimateId}/isDraft/${isDraftSelected}`;
    let url = `${this.BASE_URL}${endpoint}`;
    return this.http
      .get(url);
  }

  public getEstimateComparisonByParameter(projectEstimateId: number, compareProjectEstimateId: number): Observable<any> {
    let endpoint: string = `estimation/projects/${projectEstimateId}/compareEstimateByParameter/${compareProjectEstimateId}`;
    let url = `${this.BASE_URL}${endpoint}`;
    return this.http
      .get(url);
  }

  public getProjectEstimateAnalysis(projectId: number): Observable<any> {
    let endpoint: string = `projectestimation/estimateAnalysis/${projectId}`;
    let url = `${this.BASE_URL}${endpoint}`;
    return this.http
      .get(url);
  }

  public getProjectScheduleEstimateAnalysis(projectId: number): Observable<any> {
    let endpoint: string = `projectestimation/scheduleAnalysis/${projectId}`;
    let url = `${this.BASE_URL}${endpoint}`;
    return this.http
      .get(url);
  }

  public async generalGet(query: string): Promise<any> {
    let url = `${this.BASE_URL}${query}`;
    return this.http
      .get(url).pipe(
        catchError(e => this.error.handleHTTPError(e)))
      .toPromise()
      .then(response => response);
  }

  public generalGetObservable(query: string): Observable<any> {
    let url = `${this.BASE_URL}${query}`;
    return this.http
      .get(url);
  }

  _config = null;
  public async getConfigSettings(): Promise<any> {
    if (this._config) return this._config;

    let endpoint: string = 'config';
    let url = `${this.BASE_URL}${endpoint}`;
    return this.http
      .get(url).pipe(
        catchError(e => this.error.error('Error in getConfigSettings', e)))
      .toPromise()
      .then(response => {
        this._config = response;
        return response;
      });
  }

  public getWorkingInterest(id: any): Promise<any> {
    let table: string = 'projectheader';
    let query: string = `?projectId=${id}`;
    let url = `${this.BASE_URL}${table}${query}`;
    return this.http
      .get(url)
      .toPromise()
      .then(response => response);
  }

  public async updateWorkingInterest(projectId: number): Promise<string> {
    let totalWorkingInterest: number;
    const payload1 = await this.getWorkingInterest(projectId);
    if (payload1 < 0.0001) {
      totalWorkingInterest = null;
    } else {
      totalWorkingInterest = payload1;
    }
    const wiPatch: any = {
      WorkingInterest: totalWorkingInterest
    };
    const payload2 = await this.updateProject(projectId, wiPatch);
    return 'success';
  }

  public calculateFlowlineParameters(id: any): Promise<any> {
    let url = `${this.BASE_URL}estimation/calculateFlowlineParameters?projectid=${id}`;
    return this.http
      .get(url).pipe(
        catchError(e => this.error.error('Error in calculate flowline parameters', e)))
      .toPromise()
      .then(response => response);
  }

  public calculateWellHookupParameters(id: any, refresh: boolean): Promise<any> {
    let url = `${this.BASE_URL}estimation/calculateWellHookupParameters?projectid=${id}&refresh=${refresh}`;
    return this.http
      .get(url).pipe(
        catchError(e => this.error.error('Error in calculate well hookup parameters', e)))
      .toPromise()
      .then(response => response);
  }

  public calculateWellPadParameters(id: any, refresh: boolean): Promise<any> {
    let url = `${this.BASE_URL}estimation/calculateWellPadParameters?projectid=${id}&refresh=${refresh}`;
    return this.http
      .get(url).pipe(
        catchError(e => this.error.error('Error in calculate well pad parameters', e)))
      .toPromise()
      .then(response => response);
  }

  public calculatePadConstructionParameters(id: any, refresh: boolean): Promise<any> {
    let url = `${this.BASE_URL}estimation/calculatePadConstructionParameters?projectid=${id}&refresh=${refresh}`;
    return this.http
      .get(url).pipe(
        catchError(e => this.error.error('Error in calculating pad construction parameters', e)))
      .toPromise()
      .then(response => response);
  }

  public updateForecastOverrides(updates: any[]) {
    let patches = [];

    updates.forEach(item => {
      patches.push(this.patchEstimate(item.ProjectEstimateId, item));
    });

    zip(...patches).toPromise();
  }
  public UpdateAfeStatus(projectId: string, afenumber: string): Promise<any> {
    let url = `${this.BASE_URL}estimation/syncAfeStatus/${projectId}/${afenumber}`;
    return this.http
      .post(url, null)
      .pipe(
        catchError(err => {
          const serverMessage =
            err?.error.detail ||
            err?.error?.message ||
            err?.error?.ErrorMessage ||
            err?.message ||
            "Unknown server error";
          this.error.error(`Unable to sync AFE status: ${serverMessage}`);
          return throwError(() => serverMessage);
        })
      )
      .toPromise()
      .then(response => response);
  }
}